import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Users, ShieldCheck, ChevronRight, LogOut } from "lucide-react";
import RollerLabsLogo from "@/components/RollerLabsLogo";
import { useAuth } from "@/contexts/auth-context";
import { useVenueSwitcher } from "@/contexts/venue-switcher-context";
import { useToast } from "@/hooks/use-toast";

declare const __APP_VERSION__: string;
declare const __GIT_SHA__: string;
declare const __BUILD_DATE__: string;

const navItems = [
  { label: "Customers", icon: Users, path: "/admin" },
  { label: "Users", icon: ShieldCheck, path: "/admin/users" },
];

const SuperAdminLayoutContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { stopImpersonating } = useVenueSwitcher();
  const [collapsed, setCollapsed] = useState(false);
  const { toast } = useToast();

  // Show welcome-back toast when arriving from OTP login
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("welcome") === "1") {
      const firstName = user?.email?.split("@")[0] ?? "back";
      toast({ title: `Welcome back, ${firstName}!`, description: "Good to see you again." });
      navigate(location.pathname, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    stopImpersonating();
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className={`flex flex-col border-r bg-sidebar transition-all ${collapsed ? "w-16" : "w-60"}`}>
        <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
          {!collapsed && <RollerLabsLogo variant="light" size="sm" />}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${collapsed ? "" : "rotate-180"}`} />
          </button>
        </div>
        <nav className="mt-4 flex-1 space-y-1 px-2">
          {navItems.map((item) => {
            const active = location.pathname === item.path || (item.path === "/admin" && location.pathname.startsWith("/admin/venues"));
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  active
                    ? "bg-sidebar-primary text-sidebar-primary-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent"
                }`}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && item.label}
              </button>
            );
          })}
        </nav>
        <div className="space-y-3 border-t border-sidebar-border p-3">
          {!collapsed && (
            <div className="text-[10px] text-sidebar-foreground/40 text-center space-y-0.5">
              <p>Built by ROLLER Labs</p>
              <p title={`Build: ${__GIT_SHA__} · ${__BUILD_DATE__}`}>
                v{__APP_VERSION__} · {__GIT_SHA__}
              </p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors text-sidebar-foreground hover:bg-sidebar-accent ${collapsed ? "justify-center" : ""}`}
            title="Logout"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!collapsed && "Logout"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <div className="border-b bg-background px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Super Admin</p>
            </div>
            <div className="flex items-center gap-3">
              {user?.email && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {user.email.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const SuperAdminLayout = () => {
  return <SuperAdminLayoutContent />;
};

export default SuperAdminLayout;
